// Versão: 2.04
// Data..: 2026-10-13
// v2.04: "Tarefas" unificado como aba em Agendamentos (antes de "Meu calendário"), via
// o componente TasksView; o item "Tarefas" saiu do menu lateral. O botão "+ Nova visita"
// só aparece nas visões de visita; na aba Tarefas o botão "+ Nova tarefa" vem do TasksView.
// v2.03: 3ª visão "Meu calendário" (compromissos do Zimbra via ZimbraCalendarView) e
// título do PageHeader alinhado ao menu ("Agendamentos").
// v2.02: página em .full-bleed (ocupa toda a largura do painel, sem limite de 1600px);
// removido o maxWidth 820 que estreitava a página.
// Agendamentos: chamados com status "agendado", visitas técnicas e o calendário Zimbra.
// v2.01: editor de visita usa o Modal padrão (Esc, clicar fora, X, Fechar).

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  TicketStatus,
  VisitStatus,
  VisitStatusLabels,
  type Ticket,
  type Visit,
  type VisitInput,
  type Paginated,
} from '@makweb/shared';
import { api } from '../lib/api.js';
import { useToast } from '../context/ToastContext.js';
import { PriorityBadge } from '../components/Badge.js';
import { PageHeader } from '../components/PageHeader.js';
import { Modal } from '../components/Modal.js';
import { ZimbraCalendarView } from '../components/ZimbraCalendarView.js';
import { TasksView } from '../components/TasksView.js';

type View = 'tickets' | 'visits' | 'tasks' | 'calendar';

interface Agent {
  id: string;
  name: string;
}

// Converte um ISO em value de <input type="datetime-local"> no fuso local.
function toLocalInput(iso?: string): string {
  const d = iso ? new Date(iso) : new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}

export function SchedulePage() {
  const [view, setView] = useState<View>('tickets');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState<{ visit?: Visit } | null>(null);

  const loadVisits = () =>
    api.get<{ items: Visit[] }>('/visits')
      .then((r) => r.items.sort((a, b) => (a.scheduledAt > b.scheduledAt ? 1 : -1)))
      .catch(() => [] as Visit[])
      .then(setVisits);

  const loadTickets = () =>
    api.get<Paginated<Ticket>>(`/tickets?status=${TicketStatus.SCHEDULED}&limit=100`)
      .then((r) => r.items.filter((t) => t.scheduledAt).sort((a, b) => (a.scheduledAt! > b.scheduledAt! ? 1 : -1)))
      .catch(() => [] as Ticket[])
      .then(setTickets);

  useEffect(() => {
    Promise.all([loadTickets(), loadVisits()]).finally(() => setLoading(false));
  }, []);

  // Visitas visíveis: oculta as canceladas (mas o editor ainda permite reativar).
  const activeVisits = useMemo(() => visits.filter((v) => v.status !== VisitStatus.CANCELED), [visits]);

  // Agrupa chamados por dia.
  const ticketGroups: Record<string, Ticket[]> = {};
  for (const t of tickets) {
    const day = new Date(t.scheduledAt!).toLocaleDateString('pt-BR');
    (ticketGroups[day] ??= []).push(t);
  }
  // Agrupa visitas por dia.
  const visitGroups: Record<string, Visit[]> = {};
  for (const v of activeVisits) {
    const day = new Date(v.scheduledAt).toLocaleDateString('pt-BR');
    (visitGroups[day] ??= []).push(v);
  }

  const afterSave = () => { setEditor(null); loadVisits(); };

  return (
    <div className="full-bleed">
      <PageHeader title="Agendamentos" description="Chamados agendados, visitas técnicas e o seu calendário Zimbra, organizados por data." />

      <div className="flex items-center justify-between mb-4" style={{ flexWrap: 'wrap', gap: 'var(--s2)' }}>
        <div className="flex gap-2">
          <button className={`btn btn-sm ${view === 'tickets' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('tickets')}>📅 Chamados ({tickets.length})</button>
          <button className={`btn btn-sm ${view === 'visits' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('visits')}>📍 Visitas ({activeVisits.length})</button>
          <button className={`btn btn-sm ${view === 'tasks' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('tasks')}>✅ Tarefas</button>
          <button className={`btn btn-sm ${view === 'calendar' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('calendar')}>📆 Meu calendário</button>
        </div>
        {/* "+ Nova visita" só nas visões de visita; nas abas Tarefas e Meu calendário o botão
            de ação vem de dentro do próprio componente (TasksView / ZimbraCalendarView). */}
        {(view === 'tickets' || view === 'visits') && (
          <button className="btn btn-primary btn-sm" onClick={() => setEditor({})}>+ Nova visita</button>
        )}
      </div>

      {view === 'tasks' ? (
        <TasksView />
      ) : view === 'calendar' ? (
        <ZimbraCalendarView />
      ) : loading ? (
        <div className="flex justify-center" style={{ padding: 40 }}><span className="spinner" /></div>
      ) : view === 'tickets' ? (
        tickets.length === 0 ? (
          <div className="card empty"><div className="empty-icon">📅</div>Nenhum chamado agendado.</div>
        ) : (
          <div className="flex flex-col gap-4">
            {Object.entries(ticketGroups).map(([day, items]) => (
              <div key={day}>
                <div className="text-muted font-semibold text-sm" style={{ marginBottom: 'var(--s2)' }}>{day}</div>
                <div className="card card-flush">
                  {items.map((t) => (
                    <Link key={t.id} to={`/tickets/${t.id}`} className="flex items-center justify-between" style={{ padding: 'var(--s4)', borderBottom: '1px solid var(--border)', color: 'var(--text)' }}>
                      <div className="flex items-center gap-3">
                        <span style={{ width: 48, textAlign: 'center', fontWeight: 700, color: 'var(--purple)', background: 'var(--purple-soft)', borderRadius: 'var(--radius-sm)', padding: '6px 0', fontSize: 13 }}>
                          {new Date(t.scheduledAt!).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <div>
                          <div className="font-semibold">#{t.number} — {t.subject}</div>
                          {t.requesterName && <div className="text-dim text-xs">{t.requesterName}</div>}
                        </div>
                      </div>
                      <PriorityBadge priority={t.priority} />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        activeVisits.length === 0 ? (
          <div className="card empty"><div className="empty-icon">📍</div>Nenhuma visita agendada. Use "Nova visita" para agendar.</div>
        ) : (
          <div className="flex flex-col gap-4">
            {Object.entries(visitGroups).map(([day, items]) => (
              <div key={day}>
                <div className="text-muted font-semibold text-sm" style={{ marginBottom: 'var(--s2)' }}>{day}</div>
                <div className="card card-flush">
                  {items.map((v) => (
                    <div key={v.id} className="flex items-center justify-between" style={{ padding: 'var(--s4)', borderBottom: '1px solid var(--border)' }}>
                      <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
                        <span style={{ width: 48, textAlign: 'center', fontWeight: 700, color: 'var(--brand)', background: 'var(--brand-soft)', borderRadius: 'var(--radius-sm)', padding: '6px 0', fontSize: 13 }}>
                          {new Date(v.scheduledAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div className="font-semibold">
                            {v.ticketNumber ? <Link to={`/tickets/${v.ticketId}`}>#{v.ticketNumber}</Link> : '—'} — {v.technicianName ?? 'Técnico não definido'}
                          </div>
                          <div className="text-dim text-xs" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.address ?? 'sem endereço'}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
                        <span className={`badge ${v.status === VisitStatus.DONE ? 'badge-resolved' : 'badge-scheduled'}`}>{VisitStatusLabels[v.status]}</span>
                        <button className="btn btn-sm btn-ghost" title="Editar" onClick={() => setEditor({ visit: v })}>✏️</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {editor && <VisitEditor visit={editor.visit} onClose={() => setEditor(null)} onSaved={afterSave} />}
    </div>
  );
}

// Modal para criar/editar/reagendar/concluir/cancelar uma visita técnica.
function VisitEditor({ visit, onClose, onSaved }: { visit?: Visit; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const isEdit = !!visit;
  const [agents, setAgents] = useState<Agent[]>([]);
  const [ticketQuery, setTicketQuery] = useState('');
  const [ticketOptions, setTicketOptions] = useState<Ticket[]>([]);
  const [ticketId, setTicketId] = useState(visit?.ticketId ?? '');
  const [ticketLabel, setTicketLabel] = useState(visit ? `#${visit.ticketNumber ?? ''}` : '');
  const [scheduledAt, setScheduledAt] = useState(toLocalInput(visit?.scheduledAt));
  const [technicianId, setTechnicianId] = useState(visit?.technicianId ?? '');
  const [address, setAddress] = useState(visit?.address ?? '');
  const [notes, setNotes] = useState(visit?.notes ?? '');
  const [status, setStatus] = useState<VisitStatus>(visit?.status ?? VisitStatus.SCHEDULED);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ items: Agent[] }>('/agents').then((r) => setAgents(r.items)).catch(() => {});
  }, []);

  // Carrega chamados ativos uma vez (na criação) para busca local por número/assunto.
  const [allTickets, setAllTickets] = useState<Ticket[]>([]);
  useEffect(() => {
    if (isEdit) return;
    const statuses = [TicketStatus.OPEN, TicketStatus.IN_PROGRESS, TicketStatus.WAITING_CLIENT, TicketStatus.SCHEDULED];
    Promise.all(
      statuses.map((s) => api.get<Paginated<Ticket>>(`/tickets?status=${s}&limit=100`).catch(() => ({ items: [] as Ticket[] }))),
    ).then((res) => setAllTickets(res.flatMap((r) => r.items)));
  }, [isEdit]);

  // Filtra os chamados carregados conforme o termo digitado (mín. 2 caracteres).
  useEffect(() => {
    if (isEdit) return;
    const term = ticketQuery.trim().toLowerCase();
    if (term.length < 2) { setTicketOptions([]); return; }
    setTicketOptions(
      allTickets
        .filter((t) => `#${t.number} ${t.subject} ${t.requesterName ?? ''}`.toLowerCase().includes(term))
        .slice(0, 10),
    );
  }, [ticketQuery, isEdit, allTickets]);

  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!ticketId) return toast.error('Selecione o chamado da visita.');
    if (!scheduledAt) return toast.error('Informe a data e hora.');
    setSaving(true);
    const payload: VisitInput = {
      scheduledAt: new Date(scheduledAt).toISOString(),
      technicianId: technicianId || undefined,
      address: address || undefined,
      notes: notes || undefined,
      status,
    };
    try {
      if (isEdit && visit) {
        await api.patch(`/tickets/${visit.ticketId}/visits/${encodeURIComponent(visit.scheduledAt)}/${visit.id}`, payload);
        toast.success('Visita atualizada');
      } else {
        await api.post(`/tickets/${ticketId}/visits`, payload);
        toast.success('Visita agendada');
      }
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao salvar visita');
    } finally {
      setSaving(false);
    }
  };

  const cancelVisit = async () => {
    if (!visit) return;
    setSaving(true);
    try {
      await api.patch(`/tickets/${visit.ticketId}/visits/${encodeURIComponent(visit.scheduledAt)}/${visit.id}`, { status: VisitStatus.CANCELED });
      toast.success('Visita cancelada');
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cancelar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title={isEdit ? 'Editar visita' : 'Nova visita técnica'}
      onClose={onClose}
      maxWidth={480}
      footer={
        <>
          {isEdit ? (
            <button type="button" className="btn btn-sm btn-ghost" style={{ color: 'var(--danger)', marginRight: 'auto' }} onClick={cancelVisit} disabled={saving}>Cancelar visita</button>
          ) : null}
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Fechar</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvando...' : isEdit ? 'Salvar' : 'Agendar'}</button>
        </>
      }
    >
      <form onSubmit={save}>
        {isEdit ? (
          <div className="field">
            <label className="label">Chamado</label>
            <div className="input" style={{ background: 'var(--surface-2)' }}>{ticketLabel || `#${visit?.ticketNumber ?? ''}`}</div>
          </div>
        ) : (
          <div className="field">
            <label className="label">Chamado</label>
            {ticketId ? (
              <div className="flex items-center gap-2">
                <div className="input" style={{ flex: 1, background: 'var(--surface-2)' }}>{ticketLabel}</div>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setTicketId(''); setTicketLabel(''); }}>Trocar</button>
              </div>
            ) : (
              <>
                <input className="input" placeholder="Busque por número ou assunto (mín. 2 caracteres)" value={ticketQuery} onChange={(e) => setTicketQuery(e.target.value)} />
                {ticketOptions.length > 0 && (
                  <div className="card card-flush mt-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {ticketOptions.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className="flex flex-col"
                        style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%' }}
                        onClick={() => { setTicketId(t.id); setTicketLabel(`#${t.number} — ${t.subject}`); setTicketOptions([]); }}
                      >
                        <span className="font-semibold text-sm">#{t.number} — {t.subject}</span>
                        {t.requesterName && <span className="text-dim text-xs">{t.requesterName}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="field">
          <label className="label">Data e hora</label>
          <input className="input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} required />
        </div>
        <div className="field">
          <label className="label">Técnico</label>
          <select className="input" value={technicianId} onChange={(e) => setTechnicianId(e.target.value)}>
            <option value="">— Não definido —</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="label">Endereço</label>
          <input className="input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Local da visita" />
        </div>
        <div className="field">
          <label className="label">Observações</label>
          <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        {isEdit && (
          <div className="field">
            <label className="label">Situação</label>
            <select className="input" value={status} onChange={(e) => setStatus(e.target.value as VisitStatus)}>
              <option value={VisitStatus.SCHEDULED}>{VisitStatusLabels[VisitStatus.SCHEDULED]}</option>
              <option value={VisitStatus.DONE}>{VisitStatusLabels[VisitStatus.DONE]}</option>
              <option value={VisitStatus.CANCELED}>{VisitStatusLabels[VisitStatus.CANCELED]}</option>
            </select>
          </div>
        )}

        {/* Botão oculto permite enviar com Enter dentro do form. */}
        <button type="submit" style={{ display: 'none' }} />
      </form>
    </Modal>
  );
}
